import React, {useState, useRef} from 'react';
import { marked } from 'marked';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import DOMPurify from 'dompurify';
import exportHTML from './ExportHTML';
import indexStyles from "./styles";
import hljs from 'highlight.js';
import './codeStyles.css';


function htmlToMarkdown(html) {
    return html
        .replace(/<h1>(.*?)<\/h1>/gi, '# $1\n')  // Headers
        .replace(/<p>(.*?)<\/p>/gi, '$1\n\n')    // Paragraphs
        .replace(/<br\s*\/?>/gi, '\n')           // Line breaks
        .replace(/<strong>(.*?)<\/strong>/gi, '**$1**')  // Bold
        .replace(/<em>(.*?)<\/em>/gi, '*$1*')            // Italics
        .replace(/<u>(.*?)<\/u>/gi, '__$1__')            // Underline
        .replace(/<ul>\s*<\/ul>/gi, '')                   // Empty unordered lists
        .replace(/<ol>\s*<\/ol>/gi, '')                   // Empty ordered lists
        .replace(/<li>(.*?)<\/li>/gi, '* $1\n')           // List items
        .replace(/<ul>([\s\S]*?)<\/ul>/gi, '$1\n')        // Unordered lists
        .replace(/<ol>([\s\S]*?)<\/ol>/gi, '$1\n')        // Ordered lists
    // Add more transformations as needed
}
const renderMarkdown = (markdownText, updatedIndexStyles) => {

    // Replace HTML entities for < and > with actual symbols
    let processedText = markdownText.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');

    // Replace \[...\] with ```latex...``` for block LaTeX
    processedText = processedText.replace(/\\\[(.*?)\\\]/gs, '```latex\n$1\n```');

    const problematicLatexPattern = /```latex[\s\S]*?\\text\{\$.*?\$\}[\s\S]*?```/;

    if (problematicLatexPattern.test(processedText)) {
        return '<div class="latex-error">Nested LaTeX syntax \\text{$...$} is not supported. Please read the error guide: </div>';
    }

    processedText = processedText.replace(
        /::: dropdown \| (.*?)\n([\s\S]*?):::/g,
        (match, title, content) => `<details><summary>${title}</summary><div class="dropdown-content">${content}</div></details>`
    );


    // Custom renderer with marked
    const renderer = new marked.Renderer();

    renderer.code = (code, language) => {
        if (language === 'latex') {
            try {
                // Render block LaTeX first, skipping inline LaTeX processing
                return `<div class="latex">${katex.renderToString(code, { throwOnError: false })}</div>`;
            } catch (error) {
                return `<div class="latex-error">${error.message}</div>`;
            }
        }

        // Use highlight.js for syntax highlighting
        try {
            const highlightedCode = language ? hljs.highlight(code, { language }).value : hljs.highlightAuto(code).value;
            return `<pre><code class="hljs ${language}">${highlightedCode}</code></pre>`;
        } catch (error) {
            // Handle the error when an invalid language is specified
            console.warn(`Error highlighting code block with language "${language}":`, error);
            return `<pre><code>${code}</code></pre>`;
        }
    };

    // Process inline LaTeX after block LaTeX is processed
    processedText = processedText.replace(/(?<!\\)\$(.+?)\$/g, (match, p1) => {
        try {
            return katex.renderToString(p1, { throwOnError: false });
        } catch (error) {
            return `<span class="latex-error">${error.message}</span>`;
        }
    });

    processedText = processedText.replace(
        /::: dropdown \| (.*?)\n([\s\S]*?):::/g,
        (match, title, content) => `<details><summary>${title}</summary><div class="dropdown-content">${content}</div></details>`
    );

    processedText = processedText.split('\n').map((line, index, lines) => {
        if (line.startsWith('::: theorem')) {
            const title = line.substring(line.indexOf('|') + 1).trim();
            return `<div class="box"><div class="box-title">${title}</div>`;
        } else if (line.startsWith('::: mainpoint')) {
            let [, title, color] = line.split('|').map(part => part.trim());
            color = color || "grey"; // Default to grey if color is not specified
            return `<div class="mainpoint mainpoint-${color}"><div class="mainpoint-title mainpoint-title-${color}">${title}</div><div class="mainpoint-content">`;
        } else if (line === ':::' || line === ':::\n') {
            return `</div></div>`;
        }
        return line;
    }).join('\n');

    return marked.parse(processedText, { renderer });
}

// React component to use the method
const MarkdownRenderer = ({ content, useLatinModernFont }) => {

    let sanitized = DOMPurify.sanitize(content, {
        ALLOWED_TAGS: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'b', 'i', 'em', 'strong', 'a', 'ul', 'ol', 'li', 'p', 'br', 'span', 'img', 'div'],
        ALLOWED_ATTR: ['href', 'title', 'src', 'alt', 'class', 'style']
    });

    const previewRef = useRef(null);
    const [title, setTitle] = useState('');
    const [author, setAuthor] = useState('');

    let stripped = htmlToMarkdown(sanitized);

    const cssBlockPattern = /%%%\n([\s\S]*?)\n%%%/g;
    let customStyles = '';

    stripped = stripped.replace(cssBlockPattern, (match, cssContent) => {
        customStyles = cssContent;
        return '';
    });

// Update the styles.js file with the custom styles
    const updatedIndexStyles = indexStyles.replace(
        /body {([\s\S]*?)}/,
        (match, originalStyles) => `body {\n${originalStyles}\n${customStyles}}`
    );

    const handleExportHTML = () => {
        if (previewRef.current) {
            exportHTML(previewRef.current.innerHTML, title, author, updatedIndexStyles);
        }
    }

    React.useEffect(() => {
        try {
            hljs.highlightAll();
        } catch (error) {
            console.warn('Error highlighting code blocks:', error);
        }
    }, [content]);

    return (
        <div>
            <div className={"custom-toolbar"}>
                <input type="text" placeholder="Title" onChange={(e) => setTitle(e.target.value)} />
                <input type="text" placeholder="Author" onChange={(e) => setAuthor(e.target.value)} />
                <button onClick={handleExportHTML}>Export HTML</button>
            </div>
            <div ref = {previewRef} className="preview-container" dangerouslySetInnerHTML={{ __html: renderMarkdown(stripped) }} />
        </div>
    );
};

export default MarkdownRenderer;